import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../components/AppIcon';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useToast } from '../../context/ToastContext';
import { fetchCaterers, updateCatererStatus, blockCaterer, unblockCaterer, markCatererSuspect, clearCatererSuspect, fetchConversationWithCaterer, sendMessageToCaterer, subscribeToAdminCatererMessages, updateCatererProfile, uploadCatererImage } from '../../lib/adminApi';
import { exportToCsv, downloadFilename } from '../../lib/csvExport';

const statusFilters = [
  { value: 'all', label: 'All Caterers' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'none', label: 'None (unverified)' },
  { value: 'basic', label: 'Basic' },
  { value: 'pro', label: 'Pro' },
  { value: 'elite', label: 'Elite' },
];

const statusPillStyles = {
  active: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-amber-100 text-amber-700',
  suspended: 'bg-rose-100 text-rose-700',
};

function getCatererStats(caterer) {
  const s = caterer?.caterer_stats;
  return Array.isArray(s) ? s[0] : s;
}

const catererExportColumns = [
  { key: 'id', label: 'ID' },
  { key: 'business_name', label: 'Business' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'location', label: 'Location' },
  { key: 'verification_tier', label: 'Tier' },
  { key: 'is_suspect', label: 'Suspect' },
  { key: 'total_orders', label: 'Orders' },
  { key: 'total_revenue', label: 'Revenue' },
  { key: 'created_at', label: 'Joined' },
];

export default function CatererManagementPage() {
  const { profile } = useAdminAuth();
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [caterers, setCaterers] = useState([]);
  const [totalCaterers, setTotalCaterers] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionState, setActionState] = useState({});
  const [messageCaterer, setMessageCaterer] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [messageBody, setMessageBody] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [editCaterer, setEditCaterer] = useState(null);
  const [editForm, setEditForm] = useState({
    business_name: '',
    email: '',
    phone: '',
    location: '',
    logo_url: '',
    hero_banner_url: '',
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);

  const filteredCaterers = useMemo(() => caterers, [caterers]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, pageSize]);

  useEffect(() => {
    let isMounted = true;

    async function loadCaterers() {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchCaterers({
          status: statusFilter === 'all' ? undefined : statusFilter,
          search: debouncedSearch || undefined,
          limit: pageSize,
          offset: (currentPage - 1) * pageSize,
          withCount: true,
        });
        if (isMounted) {
          setCaterers(data.rows ?? []);
          setTotalCaterers(data.total ?? 0);
        }
      } catch (err) {
        console.error('Unable to load caterers', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load caterers');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadCaterers();

    return () => {
      isMounted = false;
    };
  }, [statusFilter, debouncedSearch, currentPage, pageSize]);

  const totalPages = Math.max(1, Math.ceil(totalCaterers / pageSize));

  const handleStatusChange = async (catererId, nextStatus) => {
    if (!profile?.user_id) {
      return;
    }

    setActionState((prev) => ({ ...prev, [catererId]: true }));
    try {
      await updateCatererStatus(catererId, nextStatus, profile.user_id);
      setCaterers((prev) => prev.map((caterer) => (
        caterer.id === catererId ? { ...caterer, verification_tier: nextStatus } : caterer
      )));
    } catch (err) {
      console.error('Failed to update caterer status', err);
      const msg = err instanceof Error ? err.message : 'Caterer status update failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setActionState((prev) => ({ ...prev, [catererId]: false }));
    }
  };

  const handleBlockCaterer = async (catererId) => {
    if (!profile?.user_id) {
      return;
    }
    const reason = prompt('Enter reason for blocking this caterer:');
    if (!reason) return;

    setActionState((prev) => ({ ...prev, [catererId]: true }));
    try {
      await blockCaterer(catererId, reason, profile.user_id);
      setCaterers((prev) => prev.map((caterer) =>
        caterer.id === catererId ? { ...caterer, is_blocked: true, blocked_at: new Date().toISOString(), blocked_reason: reason } : caterer
      ));
      toast.success('Caterer blocked successfully');
    } catch (err) {
      console.error('Failed to block caterer', err);
      toast.error(err instanceof Error ? err.message : 'Failed to block caterer');
    } finally {
      setActionState((prev) => ({ ...prev, [catererId]: false }));
    }
  };

  const handleUnblockCaterer = async (catererId) => {
    if (!profile?.user_id) {
      return;
    }

    setActionState((prev) => ({ ...prev, [catererId]: true }));
    try {
      await unblockCaterer(catererId, profile.user_id);
      setCaterers((prev) => prev.map((caterer) =>
        caterer.id === catererId ? { ...caterer, is_blocked: false, blocked_at: null, blocked_reason: null } : caterer
      ));
      toast.success('Caterer unblocked successfully');
    } catch (err) {
      console.error('Failed to unblock caterer', err);
      toast.error(err instanceof Error ? err.message : 'Failed to unblock caterer');
    } finally {
      setActionState((prev) => ({ ...prev, [catererId]: false }));
    }
  };

  const handleMarkSuspect = async (catererId) => {
    if (!profile?.user_id) {
      return;
    }
    const reason = prompt('Enter reason for marking this caterer as suspect:');
    if (!reason) return;
    setActionState((prev) => ({ ...prev, [catererId]: true }));
    try {
      await markCatererSuspect(catererId, reason, profile.user_id);
      setCaterers((prev) => prev.map((caterer) =>
        caterer.id === catererId ? { ...caterer, is_suspect: true, suspect_at: new Date().toISOString(), suspect_reason: reason } : caterer
      ));
      toast.success('Caterer marked as suspect');
    } catch (err) {
      console.error('Failed to mark caterer as suspect', err);
      toast.error(err instanceof Error ? err.message : 'Failed to mark caterer as suspect');
    } finally {
      setActionState((prev) => ({ ...prev, [catererId]: false }));
    }
  };

  const handleClearSuspect = async (catererId) => {
    if (!profile?.user_id) {
      return;
    }
    setActionState((prev) => ({ ...prev, [catererId]: true }));
    try {
      await clearCatererSuspect(catererId, profile.user_id);
      setCaterers((prev) => prev.map((caterer) =>
        caterer.id === catererId ? { ...caterer, is_suspect: false, suspect_at: null, suspect_reason: null } : caterer
      ));
      toast.success('Suspect flag cleared');
    } catch (err) {
      console.error('Failed to clear suspect flag', err);
      toast.error(err instanceof Error ? err.message : 'Failed to clear suspect flag');
    } finally {
      setActionState((prev) => ({ ...prev, [catererId]: false }));
    }
  };

  const openEditModal = (caterer) => {
    setEditCaterer(caterer);
    setEditForm({
      business_name: caterer.business_name || '',
      email: caterer.email || '',
      phone: caterer.phone || '',
      location: caterer.location || '',
      logo_url: caterer.logo_url || '',
      hero_banner_url: caterer.hero_banner_url || '',
    });
    setLogoFile(null);
    setBannerFile(null);
  };

  const handleSaveEdit = async () => {
    if (!editCaterer) return;
    setSavingEdit(true);
    try {
      const updates = { ...editForm };
      if (logoFile) {
        const logoPath = await uploadCatererImage(editCaterer.id, logoFile, 'logo');
        updates.logo_url = logoPath;
      }
      if (bannerFile) {
        const bannerPath = await uploadCatererImage(editCaterer.id, bannerFile, 'banner');
        updates.hero_banner_url = bannerPath;
      }
      await updateCatererProfile(editCaterer.id, updates);
      setCaterers((prev) =>
        prev.map((c) =>
          c.id === editCaterer.id
            ? {
                ...c,
                ...updates,
                has_logo: !!updates.logo_url,
                has_banner: !!updates.hero_banner_url,
              }
            : c
        )
      );
      toast.success('Caterer profile updated');
      setEditCaterer(null);
    } catch (err) {
      console.error('Failed to update caterer profile', err);
      toast.error(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setSavingEdit(false);
    }
  };

  const openMessageModal = async (caterer) => {
    if (!profile?.user_id || !caterer?.user_id) {
      toast.error('Cannot open conversation');
      return;
    }
    setMessageCaterer(caterer);
    setMessageBody('');
    setLoadingConversation(true);
    try {
      const msgs = await fetchConversationWithCaterer(profile.user_id, caterer.user_id);
      setConversationMessages(msgs);
    } catch (err) {
      toast.error(err?.message ?? 'Failed to load messages');
      setConversationMessages([]);
    } finally {
      setLoadingConversation(false);
    }
  };

  // Realtime: when caterer responds, refetch conversation so new message appears
  useEffect(() => {
    if (!messageCaterer?.user_id || !profile?.user_id) return;
    const unsub = subscribeToAdminCatererMessages(
      profile.user_id,
      messageCaterer.user_id,
      async () => {
        const msgs = await fetchConversationWithCaterer(profile.user_id, messageCaterer.user_id);
        setConversationMessages(msgs);
      }
    );
    return unsub;
  }, [messageCaterer?.user_id, profile?.user_id]);

  const handleSendMessageToCaterer = async () => {
    if (!messageCaterer?.user_id || !profile?.user_id || !messageBody.trim()) return;
    setSendingMessage(true);
    try {
      await sendMessageToCaterer(profile.user_id, messageCaterer.user_id, messageBody.trim());
      setMessageBody('');
      const msgs = await fetchConversationWithCaterer(profile.user_id, messageCaterer.user_id);
      setConversationMessages(msgs);
      toast.success('Message sent');
    } catch (err) {
      toast.error(err?.message ?? 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Caterer Directory</h2>
          <p className="text-sm text-muted-foreground">
            Track onboarding, readiness, and interventions for every supplier in the Mrs Cater marketplace.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              const rows = filteredCaterers.map((c) => {
                const stats = getCatererStats(c);
                return {
                  id: c.id,
                  business_name: c.business_name ?? c.name ?? '',
                  email: c.email ?? '',
                  phone: c.phone ?? '',
                  location: c.location ?? '',
                  verification_tier: c.verification_tier ?? '',
                  is_suspect: c.is_suspect ? 'yes' : 'no',
                  total_orders: stats?.total_orders ?? c.total_orders ?? '',
                  total_revenue: stats?.total_revenue ?? c.total_revenue ?? '',
                  created_at: c.created_at ? format(new Date(c.created_at), 'yyyy-MM-dd') : '',
                };
              });
              if (!rows.length) {
                toast.info('No caterers to export');
                return;
              }
              exportToCsv(rows, catererExportColumns, downloadFilename('caterers'));
              toast.success(`Exported ${rows.length} caterers`);
            }}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground shadow-sm hover:bg-muted"
          >
            <Icon name="Download" size={16} />
            Export CSV
          </button>
          <button className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90">
            <Icon name="Plus" size={16} />
            Invite Caterer
          </button>
        </div>
      </header>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-border bg-background px-3 py-2">
            <Icon name="Search" size={16} className="text-muted-foreground" />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by name or email"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto">
            {statusFilters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setStatusFilter(filter.value)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  statusFilter === filter.value ? 'bg-primary text-primary-foreground shadow' : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            Showing {(totalCaterers === 0 ? 0 : (currentPage - 1) * pageSize + 1)}-
            {Math.min(currentPage * pageSize, totalCaterers)} of {totalCaterers.toLocaleString()} caterers
          </p>
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">Rows</label>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="rounded-md border border-border bg-background px-2 py-1 text-xs"
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1 || loading}
              className="rounded-md border border-border px-2 py-1 text-xs disabled:opacity-50"
            >
              Prev
            </button>
            <span className="text-xs text-muted-foreground">
              Page {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages || loading}
              className="rounded-md border border-border px-2 py-1 text-xs disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted/30 text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Caterer</th>
                <th className="px-4 py-3 text-left font-semibold">Contact</th>
                <th className="px-4 py-3 text-left font-semibold">Location</th>
                <th className="px-4 py-3 text-left font-semibold">Setup</th>
                <th className="px-4 py-3 text-left font-semibold">Orders</th>
                <th className="px-4 py-3 text-left font-semibold">Revenue</th>
                <th className="px-4 py-3 text-left font-semibold">Joined</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/80">
              {loading && (
                <tr>
                  <td colSpan={9} className="px-6 py-6 text-center text-muted-foreground">
                    Loading caterers…
                  </td>
                </tr>
              )}

              {!loading && filteredCaterers.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-6 py-6 text-center text-muted-foreground">
                    No caterers match the current filters.
                  </td>
                </tr>
              )}

              {filteredCaterers.map((caterer) => (
                <tr key={caterer.id} className="hover:bg-muted/40">
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="font-semibold text-foreground">{caterer.business_name ?? caterer.name ?? '—'}</span>
                      <span className="text-xs text-muted-foreground">ID: {caterer.id.slice(0, 8)}…</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    <div className="flex flex-col gap-0.5">
                      <span>{caterer.email ?? '—'}</span>
                      <span>{caterer.phone ?? '—'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{caterer.location ?? caterer.city ?? '—'}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    <div className="flex flex-col gap-0.5">
                      <span>
                        Logo:{' '}
                        <span className={caterer.has_logo ? 'text-emerald-700' : 'text-amber-700'}>
                          {caterer.has_logo ? 'Present' : 'Missing'}
                        </span>
                      </span>
                      <span>
                        Banner:{' '}
                        <span className={caterer.has_banner ? 'text-emerald-700' : 'text-amber-700'}>
                          {caterer.has_banner ? 'Present' : 'Missing'}
                        </span>
                      </span>
                      <span>
                        Menu items:{' '}
                        <span className={caterer.menu_item_count > 0 ? 'text-emerald-700' : 'text-amber-700'}>
                          {caterer.menu_item_count > 0 ? caterer.menu_item_count : 'None yet'}
                        </span>
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-foreground">{Number(getCatererStats(caterer)?.total_orders ?? caterer.total_orders ?? 0).toLocaleString()}</td>
                  <td className="px-4 py-3 font-semibold text-foreground">
                    R{Number(getCatererStats(caterer)?.total_revenue ?? caterer.total_revenue ?? 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {caterer.created_at ? format(new Date(caterer.created_at), 'dd MMM yyyy') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex items-center rounded-full px-3 py-0.5 text-xs font-semibold ${
                        caterer.is_blocked ? 'bg-rose-100 text-rose-700' : (statusPillStyles[caterer.verification_tier] ?? 'bg-muted text-foreground')
                      }`}>
                        {caterer.is_blocked ? 'Blocked' : (caterer.verification_tier ?? 'none')}
                      </span>
                      {caterer.is_suspect && (
                        <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
                          Suspect
                        </span>
                      )}
                      {caterer.is_blocked && caterer.blocked_reason && (
                        <span className="mt-0.5 block max-w-xs truncate text-xs text-muted-foreground" title={caterer.blocked_reason}>{caterer.blocked_reason}</span>
                      )}
                      {caterer.is_suspect && caterer.suspect_reason && (
                        <span className="mt-0.5 block max-w-xs truncate text-xs text-muted-foreground" title={caterer.suspect_reason}>{caterer.suspect_reason}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2 flex-wrap">
                      {!caterer.is_blocked && caterer.verification_tier === 'none' && (
                        <button
                          disabled={actionState[caterer.id]}
                          onClick={() => handleStatusChange(caterer.id, 'basic')}
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
                        >
                          <Icon name="BadgeCheck" size={14} /> Approve tier
                        </button>
                      )}
                      <button
                        onClick={() => handleBlockCaterer(caterer.id)}
                        disabled={actionState[caterer.id] || caterer.is_blocked}
                        className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-3 py-1 text-xs font-semibold text-white hover:bg-rose-500 disabled:opacity-60"
                      >
                        <Icon name="Ban" size={14} /> Block
                      </button>
                      {!caterer.is_suspect && (
                        <button
                          onClick={() => handleMarkSuspect(caterer.id)}
                          disabled={actionState[caterer.id]}
                          className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-500 disabled:opacity-60"
                        >
                          <Icon name="AlertTriangle" size={14} /> Mark suspect
                        </button>
                      )}
                      {caterer.is_suspect && (
                        <button
                          onClick={() => handleClearSuspect(caterer.id)}
                          disabled={actionState[caterer.id]}
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
                        >
                          <Icon name="ShieldCheck" size={14} /> Clear suspect
                        </button>
                      )}
                      {caterer.is_blocked && (
                        <button
                          onClick={() => handleUnblockCaterer(caterer.id)}
                          disabled={actionState[caterer.id]}
                          className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-500 disabled:opacity-60"
                        >
                          <Icon name="CheckCircle" size={14} /> Unblock
                        </button>
                      )}
                      <button
                        onClick={() => openEditModal(caterer)}
                        className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 text-xs font-semibold text-muted-foreground hover:bg-muted"
                      >
                        <Icon name="Pencil" size={14} /> Edit profile
                      </button>
                      <button
                        onClick={() => openMessageModal(caterer)}
                        className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 text-xs font-semibold text-muted-foreground hover:bg-muted"
                      >
                        <Icon name="MessageSquare" size={14} /> Message
                      </button>
                      {caterer.email && (
                        <button
                          onClick={() =>
                            window.open(
                              `mailto:${caterer.email}?subject=${encodeURIComponent(
                                'Please complete your Mrs Cater profile'
                              )}&body=${encodeURIComponent(
                                `Hi ${caterer.business_name ?? ''},\n\nWe noticed your Mrs Cater profile is not fully set up yet (logo/banner/menu). Please log in to your caterer dashboard to complete your profile so customers can discover and book you.\n\nThank you,\nMrs Cater Team`
                              )}`,
                              '_blank'
                            )
                          }
                          className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 text-xs font-semibold text-muted-foreground hover:bg-muted"
                        >
                          <Icon name="Mail" size={14} /> Nudge
                        </button>
                      )}
                      <button
                        onClick={() =>
                          window.open(
                            `https://www.mrscater.com/caterer-profile/${encodeURIComponent(caterer.id)}`,
                            '_blank'
                          )
                        }
                        className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 text-xs font-semibold text-muted-foreground hover:bg-muted"
                      >
                        <Icon name="ExternalLink" size={14} /> View
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Message caterer modal */}
      {messageCaterer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setMessageCaterer(null)}>
          <div className="w-full max-w-lg rounded-xl border border-border bg-card shadow-xl flex flex-col max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h3 className="font-semibold text-foreground">Message {messageCaterer.business_name ?? messageCaterer.name ?? 'Caterer'}</h3>
              <button type="button" onClick={() => setMessageCaterer(null)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 min-h-[200px]">
              {loadingConversation ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : conversationMessages.length === 0 ? (
                <p className="text-sm text-muted-foreground">No messages yet. Send a message below.</p>
              ) : (
                conversationMessages.map((m) => (
                  <div
                    key={m.id}
                    className={`rounded-lg p-3 max-w-[90%] ${m.sender_id === profile?.user_id ? 'ml-auto bg-primary/15' : 'bg-muted'}`}
                  >
                    <p className="text-xs font-medium text-muted-foreground">{m.sender_id === profile?.user_id ? 'You' : messageCaterer.business_name ?? 'Caterer'}</p>
                    <p className="text-sm text-foreground mt-0.5">{m.body ?? m.subject}</p>
                    <p className="text-xs text-muted-foreground mt-1">{m.created_at ? format(new Date(m.created_at), 'dd MMM HH:mm') : ''}</p>
                  </div>
                ))
              )}
            </div>
            <div className="border-t border-border p-4">
              <textarea
                value={messageBody}
                onChange={(e) => setMessageBody(e.target.value)}
                placeholder="Type your message…"
                rows={3}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
              />
              <div className="mt-2 flex justify-end gap-2">
                <button type="button" onClick={() => setMessageCaterer(null)} className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted">
                  Close
                </button>
                <button
                  type="button"
                  onClick={handleSendMessageToCaterer}
                  disabled={sendingMessage || !messageBody.trim()}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                >
                  {sendingMessage ? 'Sending…' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Edit caterer profile modal */}
      {editCaterer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setEditCaterer(null)}
        >
          <div
            className="w-full max-w-xl rounded-xl border border-border bg-card shadow-xl flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h3 className="font-semibold text-foreground">
                Edit profile – {editCaterer.business_name ?? editCaterer.name ?? 'Caterer'}
              </h3>
              <button
                type="button"
                onClick={() => setEditCaterer(null)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  Business name
                </label>
                <input
                  type="text"
                  value={editForm.business_name}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, business_name: e.target.value }))
                  }
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, email: e.target.value }))
                    }
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Phone
                  </label>
                  <input
                    type="text"
                    value={editForm.phone}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, phone: e.target.value }))
                    }
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  Location (displayed to customers)
                </label>
                <input
                  type="text"
                  value={editForm.location}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, location: e.target.value }))
                  }
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Logo file (upload new)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                    className="w-full text-xs"
                  />
                  {editForm.logo_url && !logoFile && (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Current path: <code>{editForm.logo_url}</code>
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Cover / banner file (upload new)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setBannerFile(e.target.files?.[0] || null)}
                    className="w-full text-xs"
                  />
                  {editForm.hero_banner_url && !bannerFile && (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Current path: <code>{editForm.hero_banner_url}</code>
                    </p>
                  )}
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                You can upload new logo and cover images directly here. Files are stored in the
                <code> profile-images</code> bucket under <code>caterers/&lt;catererId&gt;/…</code>.
              </p>
            </div>
            <div className="border-t border-border p-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditCaterer(null)}
                className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={savingEdit}
                className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                {savingEdit ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
